import React from 'react';
import { X } from 'lucide-react';
import type { CatalogRule } from '../../types';

const STATUS_COLORS: Record<string, string> = {
  production:   'bg-green-500/20 text-green-400 border-green-500/30',
  beta:         'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  experimental: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  requested:    'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

interface Props {
  rule: CatalogRule;
  onClose: () => void;
}

const RuleDetailModal: React.FC<Props> = ({ rule, onClose }) => {
  const statusCls = STATUS_COLORS[rule.status.toLowerCase()] || 'bg-cargo-panel text-cargo-muted border-cargo-border';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-cargo-border">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-cargo-text font-mono break-all">{rule.rule}</h2>
            <p className="text-xs text-cargo-muted mt-1">{rule.description}</p>
          </div>
          <button onClick={onClose} className="ml-4 text-cargo-muted hover:text-cargo-text flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Long description */}
          {rule.long_description && (
            <div>
              <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider mb-1">Description</h3>
              <p className="text-sm text-cargo-text leading-relaxed">{rule.long_description}</p>
            </div>
          )}

          {/* Tags */}
          {rule.tags.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider mb-2">Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {rule.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-full bg-cargo-accent/10 text-cargo-accent border border-cargo-accent/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Supported backends */}
          {rule.supported_backend.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider mb-2">Supported Backends</h3>
              <div className="flex flex-wrap gap-1.5">
                {rule.supported_backend.map((be) => (
                  <span
                    key={be}
                    className="px-2.5 py-1 text-xs rounded-full bg-cargo-panel text-cargo-muted border border-cargo-border"
                  >
                    {be}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Parameters table */}
          {rule.args.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider mb-2">Parameters</h3>
              <div className="border border-cargo-border rounded-lg overflow-hidden">
                <table className="w-full text-sm table-fixed">
                  <thead>
                    <tr className="bg-cargo-panel">
                      <th className="w-[22%] px-3 py-2 text-left text-xs font-medium text-cargo-muted">Name</th>
                      <th className="w-[14%] px-3 py-2 text-left text-xs font-medium text-cargo-muted">Type</th>
                      <th className="w-[10%] px-3 py-2 text-center text-xs font-medium text-cargo-muted">Required</th>
                      <th className="w-[54%] px-3 py-2 text-left text-xs font-medium text-cargo-muted">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.args.map((arg) => (
                      <tr
                        key={arg.name}
                        className="border-t border-cargo-border/50 hover:bg-cargo-panel/30"
                      >
                        <td className="px-3 py-2 font-mono text-cargo-text text-xs break-all">{arg.name}</td>
                        <td className="px-3 py-2 text-cargo-muted text-xs">{arg.type}</td>
                        <td className="px-3 py-2 text-center">
                          {arg.mandatory === 'yes' ? (
                            <span className="text-xs font-medium text-red-400">Yes</span>
                          ) : (
                            <span className="text-xs text-cargo-muted/50">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-cargo-muted text-xs break-words">{arg.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-cargo-border">
          <div className="flex items-center gap-4 text-xs text-cargo-muted">
            <span>Owner: <span className="text-cargo-text">{rule.owner}</span></span>
            <span>Category: <span className="text-cargo-text">{rule.category}</span></span>
            {rule.package && <span>Package: <span className="text-cargo-text">{rule.package}</span></span>}
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border ${statusCls}`}>
            {rule.status}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RuleDetailModal;
